import {ITeamsContextState} from "./TeamsContext.interfaces";
import {useCallback, useEffect, useRef, useState} from "react";
import * as microsoftTeams from "@microsoft/teams-js";
import {FrameContexts, HostClientType, UserTeamRole} from "@microsoft/teams-js";
import moment from "moment";
import 'moment/locale/fr';
import {mergeThemes, teamsDarkTheme, teamsHighContrastTheme, teamsTheme} from "@fluentui/react-northstar";
import languageObject from "../../translations";
import {TokenModule} from "../../modules/Token.module";
import {IConfigurationContext} from "../ConfigurationContext/ConfigurationContext.interfaces";
import {initTeamsFx} from "../../auth/TeamsFx/TeamsFx";
import {TokenApi} from "../../apis/tokenApi";
import {ThemeCustomizations} from "../../const/ThemeCustomizations";

const initialState: ITeamsContextState = {
    loaded: false,
    initialized: false,
    locale: "en",
    fullLocale: "en-US",
    teamsTheme: teamsTheme,
    themeClass: "defaultContainer",
    groupId: "",
    channelId: "",
    teamId: "",
    teamName: "",
    tenantId: "",
    channelName: "",
    userMail: "",
    userName: "",
    userId: "",
    hostClientType: "",
    isConfiguringApp: false,
    isDeletingApp: false,
    isOnMobile: false,
    isInPersonnalApp: false,
    subEntityId: "",
    isInTaskModule: false,
    entityId: "",
    isInMeeting: false,
    meetingId: "",
    isInMeetingSidePanel: false,
    userRole: UserTeamRole.User,
    isTouchScreen: false,
    isTeamsApp: true,
    isTeamsIframe: true,
}

export const useTeamsContext = (configurationContext: IConfigurationContext) => {
    const [state, setState] = useState<ITeamsContextState>(initialState);
    const listenerFireTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
    const lastEventTargetRef = useRef<EventTarget | null>();
    const isTouchScreenRef = useRef<boolean>(false);

    useEffect(function onMount() {
        if (!configurationContext.loaded) return;
        (async () => {
            const isTeamsApp = await TokenModule.isAppFromTeams();
            if (isTeamsApp) await initializeFromTeams();
            else await initializeOutsideOfTeams();
            setListeners();
        })();
        return () => {
            document.body.removeEventListener("mousedown", handleMouseDown);
            document.body.removeEventListener("touchstart", handleTouchStart);
        }
    }, [configurationContext.loaded]);

    const isListenerTimeoutActive = useCallback(() => {
        if (listenerFireTimeoutRef.current !== undefined) return true;
        listenerFireTimeoutRef.current = setTimeout(() => {
            listenerFireTimeoutRef.current = undefined;
        }, 500);
        return false;
    }, []);

    const handleMouseDown = useCallback((e: MouseEvent) => {
        const isSameTarget = e.target === lastEventTargetRef.current;
        lastEventTargetRef.current = e.target;
        if (isSameTarget || !isTouchScreenRef.current || isListenerTimeoutActive()) return;
        isTouchScreenRef.current = false;
        setState((prevState): ITeamsContextState => ({...prevState, isTouchScreen: false}));
    }, []);

    const handleTouchStart = useCallback((e: TouchEvent) => {
        const isSameTarget = e.target === lastEventTargetRef.current;
        lastEventTargetRef.current = e.target;
        if (isSameTarget || isTouchScreenRef.current || isListenerTimeoutActive()) return;
        isTouchScreenRef.current = true;
        setState((prevState): ITeamsContextState => ({...prevState, isTouchScreen: true}));
    }, []);

    const setListeners = useCallback(() => {
        document.body.addEventListener("mousedown", handleMouseDown);
        document.body.addEventListener("touchstart", handleTouchStart);
    }, []);

    const initializeFromTeams = async () => {
        await microsoftTeams.app.initialize();

        microsoftTeams.app.registerOnThemeChangeHandler((theme) => {
            setTheme(theme);
        });

        const context = await microsoftTeams.app.getContext();

        moment.locale(context.app.locale);

        const stateUpdate: ITeamsContextState = {
            ...state,
            locale: context.app.locale?.substring(0, 2).toLowerCase(),
            fullLocale: context.app.locale as string,
            groupId: context.team?.groupId as string,
            channelId: context.channel?.id as string,
            hostClientType: context.app.host.clientType as string,
            teamId: context.team?.internalId as string,
            teamName: context.team?.displayName as string,
            tenantId: context.user?.tenant?.id as string,
            channelName: context.channel?.displayName as string,
            userMail: (context.user?.loginHint as string).toLowerCase(),
            userId: context.user?.id as string,
            isOnMobile: [HostClientType.ios, HostClientType.android, HostClientType.ipados]
                .includes(context.app.host.clientType),
            isInPersonnalApp: context.page.id === "eActions_personnal",
            subEntityId: context.page.subPageId as string,
            isConfiguringApp: context.page.frameContext === FrameContexts.settings,
            isDeletingApp: context.page.frameContext === FrameContexts.remove,
            isInTaskModule: context.page.frameContext === microsoftTeams.FrameContexts.task,
            userName: await getUserName(),
            meetingId: context.meeting?.id ?? "",
            isInMeeting: !!context.meeting?.id,
            isInMeetingSidePanel: context.page.frameContext === FrameContexts.sidePanel,
            userRole: context.team?.userRole ?? UserTeamRole.User,
            initialized: true,
            isTeamsApp: true,
            isTeamsIframe: true,
        }

        languageObject.locale = stateUpdate.locale;

        if (stateUpdate.isOnMobile) document.body.className = "on-mobile";

        stateUpdate.isTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        isTouchScreenRef.current = stateUpdate.isTouchScreen;

        if (stateUpdate.isConfiguringApp) stateUpdate.entityId = await getEntityId();

        setState((prevState): ITeamsContextState => ({...prevState, ...stateUpdate}));
        setTheme(context.app.theme ?? "");

        if (!stateUpdate.isInTaskModule) {
            // Save token after 5s to let app download all important data first
            setTimeout(async () => await TokenApi.saveToken(), 5000);
        }
    }

    const initializeOutsideOfTeams = async () => {
        if (window.location.pathname === "/") window.location.replace("/messages");
        const tokenData = await TokenModule.getAccessToken();
        if (!tokenData) throw new Error("token is empty");
        setTheme("default");
        const fourLettersLocale = window.navigator.language;
        const twoLettersLocale = fourLettersLocale.slice(0, 2).toLowerCase();
        languageObject.locale = twoLettersLocale;
        moment.locale(fourLettersLocale);
        const parentUrl = (window.location != window.parent.location) ? document.referrer : document.location.href;
        const isTeamsIframe = parentUrl.includes("teams.cloud.microsoft");
        setState((prevState): ITeamsContextState => ({
            ...prevState,
            isTeamsApp: false,
            isTeamsIframe,
            initialized: true,
            tenantId: tokenData.tenantId ?? "",
            userId: tokenData.userId ?? "",
            userMail: tokenData.userMail ?? "",
            userName: tokenData.userName ?? "",
            locale: twoLettersLocale,
            fullLocale: fourLettersLocale,
        }));
        // Save token after 5s to let app download all important data first
        setTimeout(async () => await TokenApi.saveToken(), 5000);
    }

    const initializeTeamsFx = async (clientId: string, graphScopes: Array<string>) => {
        if (!clientId || !graphScopes) return;
        await initTeamsFx({
            clientId: clientId,
            apiBaseUrl: "https://" + window.location.host,
            scopes: graphScopes,
            inTeams: state.isTeamsApp,
            locale: state.locale,
            isWidget: false,
            isOnMobile: state.isOnMobile,
            theme: state.themeClass,
            tenantId: state.tenantId
        });
        setState((prevState): ITeamsContextState => ({...prevState, loaded: true}));
    }

    const getEntityId = async () => {
        const settings = await microsoftTeams.pages.getConfig();
        return settings.entityId ?? "";
    }

    const getUserName = async () => {
        const tokenResult = await TokenModule.getAccessToken();
        if (!tokenResult) throw new Error("token is empty");
        if (tokenResult.token) {
            const tokenData = parseJWTToken(tokenResult.token);
            return tokenData.name;
        } else {
            throw new Error("Can't get tokenResult of user")
        }
    };

    const parseJWTToken = (token: string) => {
        const base64Url = token.split('.')[1];
        if (!base64Url) throw new Error("Token base64 url is undefined");
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload);
    }

    const setTheme = (value: string) => {
        let currentTheme = teamsTheme;
        let themeClass = "defaultContainer";
        switch (value) {
            case "dark":
                currentTheme = teamsDarkTheme;
                themeClass = "darkContainer";
                break;
            case "contrast":
                currentTheme = teamsHighContrastTheme;
                themeClass = "highContrastContainer";
                break;
            default:
                break;
        }
        currentTheme = mergeThemes(currentTheme, ThemeCustomizations);
        document.body.id = themeClass;
        setState(prevState => ({...prevState, teamsTheme: currentTheme, themeClass}));
    }

    return {
        ...state,
        setTheme,
        isLightTheme: state.themeClass === "defaultContainer",
        initializeTeamsFx,
    }
}